import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Stack, Group, Title, Button, Center } from "@mantine/core";
import { PageLoader, LoadingSpinner } from "@/components/common";
import { ApartmentGrid, ApartmentToolbar, ApartmentMapView } from "@/components/apartments";
import type { ViewMode } from "@/components/apartments/ApartmentToolbar";
import { apartmentsApi, settingsApi } from "@/api/client";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import type { ApartmentSearchParams, SortField, SortOrder } from "@/api/types";

const statusFilters = [
  { value: undefined, label: "All" },
  { value: "new", label: "New" },
  { value: "stale", label: "Stale" },
  { value: "contacted", label: "Contacted" },
  { value: "applied", label: "Applied" },
  { value: "rejected", label: "Rejected" },
  { value: "removed", label: "Removed" },
] as const;

type SearchParams = ApartmentSearchParams & { view?: ViewMode };

export const Route = createFileRoute("/_authenticated/apartments/")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    status: search.status as string | undefined,
    search: search.search as string | undefined,
    sort_by: (search.sort_by as SortField) || undefined,
    sort_order: (search.sort_order as SortOrder) || undefined,
    min_rent: search.min_rent ? Number(search.min_rent) : undefined,
    max_rent: search.max_rent ? Number(search.max_rent) : undefined,
    min_size: search.min_size ? Number(search.min_size) : undefined,
    max_size: search.max_size ? Number(search.max_size) : undefined,
    min_rooms: search.min_rooms ? Number(search.min_rooms) : undefined,
    max_rooms: search.max_rooms ? Number(search.max_rooms) : undefined,
    city: search.city as string | undefined,
    area: search.area as string | undefined,
    available_from: search.available_from as string | undefined,
    available_until: search.available_until as string | undefined,
    view: (search.view as ViewMode) || "list",
  }),
  component: ApartmentsPage,
});

function ApartmentsPage() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const updateSearchParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        search: (prev) => {
          const newParams = { ...prev, ...updates };
          return Object.fromEntries(
            Object.entries(newParams).filter(([, v]) => v !== undefined)
          ) as SearchParams;
        },
      });
    },
    [navigate]
  );

  // Hide "removed" apartments by default (when no specific status filter is active)
  const excludeStatus = searchParams.status ? undefined : "removed";

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["apartments", searchParams, excludeStatus],
    queryFn: ({ pageParam = 1 }) =>
      apartmentsApi.list({
        ...searchParams,
        exclude_status: excludeStatus,
        page: pageParam,
        page_size: 20,
      }),
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
  });

  const { data: priceChanges } = useQuery({
    queryKey: ["apartments", "price-changes"],
    queryFn: () => apartmentsApi.getRecentPriceChanges(24),
  });

  const { data: filterOptions } = useQuery({
    queryKey: ["apartments", "filter-options"],
    queryFn: () => apartmentsApi.getFilterOptions(),
  });

  // Map view query - only fetch when map view is active
  const view = searchParams.view || "list";
  const { data: mapApartments, isLoading: isMapLoading } = useQuery({
    queryKey: ["apartments", "map", searchParams],
    queryFn: () => apartmentsApi.listForMap({ ...searchParams, exclude_status: excludeStatus }),
    enabled: view === "map",
  });

  // Settings for office location
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsApi.get(),
    enabled: view === "map",
  });

  const officeCoords = useMemo(() => {
    if (!settings?.office_latitude || !settings?.office_longitude) {
      return null;
    }
    return {
      latitude: parseFloat(settings.office_latitude),
      longitude: parseFloat(settings.office_longitude),
    };
  }, [settings?.office_latitude, settings?.office_longitude]);

  const { lastElementRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: () => fetchNextPage(),
  });

  const apartments = data?.pages.flatMap((page) => page.items) || [];
  const total = data?.pages[0]?.total ?? 0;

  const handleSearchChange = useCallback(
    (search: string) => {
      updateSearchParams({ search: search || undefined });
    },
    [updateSearchParams]
  );

  const handleSortChange = useCallback(
    (sortBy: SortField, sortOrder: SortOrder) => {
      updateSearchParams({ sort_by: sortBy, sort_order: sortOrder });
    },
    [updateSearchParams]
  );

  const handleFilterChange = useCallback(
    (filters: Partial<ApartmentSearchParams>) => {
      updateSearchParams(filters);
    },
    [updateSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    updateSearchParams({
      min_rent: undefined,
      max_rent: undefined,
      min_size: undefined,
      max_size: undefined,
      min_rooms: undefined,
      max_rooms: undefined,
      city: undefined,
      area: undefined,
      available_from: undefined,
      available_until: undefined,
    });
  }, [updateSearchParams]);

  const handleViewChange = useCallback(
    (newView: ViewMode) => {
      updateSearchParams({ view: newView });
    },
    [updateSearchParams]
  );

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Stack
      gap="md"
      mih={
        view === "map"
          ? {
              base: "calc(100dvh - (2 * var(--app-shell-padding)) - 52px)",
              sm: "calc(100dvh - (2 * var(--app-shell-padding)))",
            }
          : undefined
      }
    >
      <Group justify="space-between">
        <Title order={1}>Apartments</Title>
      </Group>

      <Group gap="xs">
        {statusFilters.map((filter) => (
          <Button
            key={filter.value || "all"}
            component={Link}
            to="/apartments"
            search={(prev) => ({ ...prev, status: filter.value })}
            variant={searchParams.status === filter.value ? "filled" : "filled"}
            color={searchParams.status === filter.value ? "primary" : "#0f3460"}
            radius="xl"
            size="sm"
          >
            {filter.label}
          </Button>
        ))}
      </Group>

      <ApartmentToolbar
        searchParams={searchParams}
        filterOptions={filterOptions}
        total={total}
        view={view}
        onViewChange={handleViewChange}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {view === "list" ? (
        <>
          <ApartmentGrid
            apartments={apartments}
            priceChanges={priceChanges}
            lastElementRef={lastElementRef}
          />
          {isFetchingNextPage && (
            <Center py="xl">
              <LoadingSpinner />
            </Center>
          )}
        </>
      ) : (
        <ApartmentMapView
          apartments={mapApartments || []}
          isLoading={isMapLoading}
          officeCoords={officeCoords}
        />
      )}
    </Stack>
  );
}
